import { supabase } from '../lib/supabase';

export interface EmailOptions {
  to: string | string[];
  subject: string;
  html?: string;
  emailType?: 
    | 'interview_confirmation' |'interview_reminder' |'scorecard_submitted' |'candidate_status_update' |'interviewer_invitation';
  data?: any;
}

export interface InterviewConfirmationData {
  candidateName: string;
  jobTitle: string;
  organizationName: string;
  interviewType: string;
  scheduledAt: string;
  duration: number;
  interviewerName: string;
  meetingLink?: string;
  meetingPassword?: string;
}

export interface InterviewReminderData {
  recipientName: string;
  jobTitle: string;
  scheduledAt: string;
  duration: number;
  role: 'candidate' | 'interviewer';
  otherPartyName: string;
  meetingLink?: string;
}

export interface ScorecardSubmittedData {
  employerName: string;
  candidateName: string;
  overallRating: number;
  recommendation: string;
  dashboardLink?: string;
}

export interface CandidateStatusUpdateData {
  candidateName: string;
  jobTitle: string;
  organizationName: string;
  newStatus: string;
  message?: string;
}

export interface InterviewerInvitationData {
  interviewerName: string;
  jobTitle: string;
  organizationName: string;
  candidateName: string;
  scheduledAt: string;
  duration: number;
  interviewType: string;
  acceptLink: string;
}

class EmailService {
  private edgeFunctionUrl: string;

  constructor() {
    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
    this.edgeFunctionUrl = `${supabaseUrl}/functions/v1/send-email`;
  }

  /**
   * Send an email using Resend via Supabase Edge Function
   */
  async sendEmail(options: EmailOptions): Promise<{ success: boolean; messageId?: string; error?: string }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(this.edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`
        },
        body: JSON.stringify({
          to: Array.isArray(options.to) ? options.to : [options.to],
          subject: options.subject,
          html: options.html,
          emailType: options.emailType,
          data: options.data
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send email');
      }

      const result = await response.json();
      return { success: true, messageId: result.messageId };
    } catch (error) {
      console.error('Email service error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  }

  /**
   * Send interview confirmation email to candidate
   */
  async sendInterviewConfirmation(
    candidateEmail: string,
    data: InterviewConfirmationData
  ): Promise<{ success: boolean; error?: string }> {
    const result = await this.sendEmail({
      to: candidateEmail,
      subject: `Interview Confirmed: ${data.jobTitle} at ${data.organizationName}`,
      emailType: 'interview_confirmation',
      data
    });

    return { success: result.success, error: result.error };
  }

  /**
   * Send interview reminder to candidate or interviewer
   */
  async sendInterviewReminder(
    recipientEmail: string,
    data: InterviewReminderData
  ): Promise<{ success: boolean; error?: string }> {
    const result = await this.sendEmail({
      to: recipientEmail,
      subject: `Reminder: Interview for ${data.jobTitle} - ${data.scheduledAt}`,
      emailType: 'interview_reminder',
      data
    });

    return { success: result.success, error: result.error };
  }

  /**
   * Send scorecard submission notification to employer
   */
  async sendScorecardNotification(
    employerEmail: string,
    data: ScorecardSubmittedData
  ): Promise<{ success: boolean; error?: string }> {
    const result = await this.sendEmail({
      to: employerEmail,
      subject: `Scorecard Submitted for ${data.candidateName}`,
      emailType: 'scorecard_submitted',
      data
    });

    return { success: result.success, error: result.error };
  }

  /**
   * Send candidate status update notification
   */
  async sendCandidateStatusUpdate(
    candidateEmail: string,
    data: CandidateStatusUpdateData
  ): Promise<{ success: boolean; error?: string }> {
    const result = await this.sendEmail({
      to: candidateEmail,
      subject: `Application Status Update: ${data.jobTitle}`,
      emailType: 'candidate_status_update',
      data
    });

    return { success: result.success, error: result.error };
  }

  /**
   * Send interviewer invitation email
   */
  async sendInterviewerInvitation(
    interviewerEmail: string,
    data: InterviewerInvitationData
  ): Promise<{ success: boolean; error?: string }> {
    const result = await this.sendEmail({
      to: interviewerEmail,
      subject: `Interview Invitation: ${data.jobTitle} at ${data.organizationName}`,
      emailType: 'interviewer_invitation',
      data
    });

    return { success: result.success, error: result.error };
  }

  /**
   * Send custom email with HTML content
   */
  async sendCustomEmail(
    to: string | string[],
    subject: string,
    html: string
  ): Promise<{ success: boolean; error?: string }> {
    const result = await this.sendEmail({
      to,
      subject,
      html
    });

    return { success: result.success, error: result.error };
  }
}

export const emailService = new EmailService();